/* eslint-disable sort-keys */
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import classnames from 'classnames';
import flow from 'lodash/fp/flow';
import omit from 'lodash/fp/omit';
import set from 'lodash/fp/set';
import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Control, useForm, UseFormRegister } from 'react-hook-form';
import { toast } from 'react-toastify';
import MultiSelectFilter from 'v2/Filters/MultiSelectFilter/MultiSelectFilter';
import Input from 'v2/form/Input/Input';
import InputTags from 'v2/form/InputTags/InputTags';
import PriceRange, { PriceRangeRanges } from 'v2/form/PriceRange/PriceRange';
import TextArea from 'v2/form/TextArea/TextArea';
import {
  DATA_PRODUCT_DEFAULT_VALUES,
  DATA_PRODUCT_DELIVERY_FORMATS,
  parsedDataToDisplay,
  TAG_FIELDS,
} from 'v2/utils/dataProducts';
import { transformNonRequiredNumber } from 'v2/utils/formValidator';
import restFactory from 'v2/utils/restFactory';
import * as yup from 'yup';
import {
  DataProductEntity,
  DataProductFormData,
  Tag,
  TagForm,
} from '../models';
import styles from './dataProductModal.module.scss';
import { useFeatureFlagContext } from 'v2/context/featureFlagContext';
import { FEATURE_FLAGS } from 'utils/constants';
import CheckDescription, {
  DescriptionType,
} from 'v2/pages/CreateSearch/BasicInformationStep/CheckDescription';
import { CreateSearchFormData } from 'v2/pages/CreateSearch/CreateSearchSchema';
import SubmitButtonTooltip from 'v2/pages/DataRequests/CheckResponse/SubmitButtonTooltip';
import { createPromptHistoryRelatedEntity } from 'v2/queries/createPromptHistoryRelatedEntity';

interface DataProductModalProps {
  show: boolean;
  data?: DataProductFormData | null;
  onHide: (refreshData?: boolean) => void;
  title?: string;
}

const POSITIVE_NUMBER_ERR = 'Must be a positive number';
const INTEGER_NUMBER_ERR = 'Must be an integer';

const getSchema = (isCheckDescriptionEnabled: boolean) =>
  yup.object().shape({
    name: yup.string().required('Required'),
    description: yup.string().required('Required'),
    prompt_history_id: yup.number().optional(),
    check_description_status: yup.string().when('description', {
      is: (description: string) => description && isCheckDescriptionEnabled,
      then: (schema) =>
        schema.required('Please check the description to continue.'),
      otherwise: (schema) => schema.optional(),
    }),
    pricemin: yup
      .number()
      .transform(transformNonRequiredNumber)
      .moreThan(-1, POSITIVE_NUMBER_ERR)
      .integer(INTEGER_NUMBER_ERR)
      .notRequired(),
    pricemax: yup
      .number()
      .transform(transformNonRequiredNumber)
      .moreThan(yup.ref('pricemin'), 'Must be greather than Price min.')
      .integer(INTEGER_NUMBER_ERR),
    lag_in_days: yup
      .number()
      .transform(transformNonRequiredNumber)
      .moreThan(-1, POSITIVE_NUMBER_ERR)
      .integer(INTEGER_NUMBER_ERR),
    history_in_months: yup
      .number()
      .transform(transformNonRequiredNumber)
      .moreThan(-1, POSITIVE_NUMBER_ERR)
      .integer(INTEGER_NUMBER_ERR),
  });

const DataProductModal = ({
  show,
  data,
  onHide,
  title,
}: DataProductModalProps) => {
  const { flags } = useFeatureFlagContext();
  const isCheckDescriptionEnabled =
    flags && flags[FEATURE_FLAGS.CHECK_DATA_PRODUCT_DESCRIPTION];
  const schema = getSchema(isCheckDescriptionEnabled);
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useForm<DataProductFormData>({
    mode: 'onChange',
    defaultValues: parsedDataToDisplay(data || DATA_PRODUCT_DEFAULT_VALUES),
    resolver: yupResolver(schema),
  });
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (formData: DataProductFormData) => {
    const parsedData = parseDataToSave(formData);
    try {
      setIsLoading(true);
      await saveDataProduct({ id: data?.id, ...parsedData });
      setIsLoading(false);
      onHide(true);
    } catch (error) {
      toast.error(
        (error as AxiosError) ||
          `Product data can not be saved. Please contact support.`
      );
      setIsLoading(false);
    }
  };

  const checkDescription = watch('check_description_status');

  const showTooltip = () => {
    if (isCheckDescriptionEnabled && !checkDescription && !isLoading)
      return `You must check your description first before you can proceed to ${
        data ? 'save' : 'submit'
      }.`;
  };

  const modalTitle = title || (data ? 'Edit Data Product' : 'New Data Product');
  const convertedControl = control as Control<
    CreateSearchFormData | DataProductFormData
  >;
  return (
    <Modal show={show} onHide={() => onHide()} animation={false} size="lg">
      <Modal.Header>
        <Modal.Title className={classnames('modalTitle', 'pl-3')}>
          {modalTitle}
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className={classnames('card-body', styles.modalBody, 'scroll')}>
            <div className="row">
              <div className="col-md-12">
                <div className="form-group">
                  <Input
                    label="Name"
                    type="text"
                    register={register('name', { required: true })}
                    error={errors?.name?.message}
                    maxLength={64}
                    autoComplete="off"
                    required={true}
                  />
                </div>
                <div className="form-group">
                  <TextArea
                    label="Description"
                    register={register('description', { required: true })}
                    error={errors?.description?.message}
                    rows={10}
                    autoComplete="off"
                    required={true}
                  />
                  <CheckDescription
                    control={convertedControl}
                    errors={errors}
                    type={DescriptionType.DATA_PRODUCT}
                  />
                </div>
                <div className="form-group">
                  <InputTags
                    label="Sector"
                    name="sector"
                    control={control}
                    tooltip="Type a sector and press return to capture it."
                  />
                </div>
                <div className="form-group">
                  <InputTags
                    label="Metrics"
                    name="metric"
                    control={control}
                    tooltip="Type a metric and press return to capture it."
                  />
                </div>
                <div className="form-group">
                  <InputTags
                    label="Entities"
                    name="entity"
                    control={control}
                    tooltip="Type a entity and press return to capture it."
                  />
                </div>
                <div className="form-group">
                  <InputTags
                    label="Geographies"
                    name="geography"
                    control={control}
                    tooltip="Type a geography and press return to capture it."
                  />
                </div>
                <div className={classnames('form-group')}>
                  <PriceRange
                    label="Price Range (USD)"
                    register={
                      register as unknown as UseFormRegister<PriceRangeRanges>
                    }
                    error={
                      errors?.pricemin?.message || errors?.pricemax?.message
                    }
                  />
                </div>
                <div className="form-group">
                  <Input
                    className="input-number-hide-arrow"
                    label="Lag in days"
                    type="number"
                    register={register('lag_in_days', { required: true })}
                    error={errors?.lag_in_days?.message}
                  />
                </div>
                <div className="form-group">
                  <Input
                    className="input-number-hide-arrow"
                    label="History in months"
                    type="number"
                    register={register('history_in_months', { required: true })}
                    error={errors?.history_in_months?.message}
                  />
                </div>
                <div className="form-group">
                  <InputTags
                    label="Source type"
                    name="source_type"
                    control={control}
                    tooltip="Type a source type and press return to capture it."
                  />
                </div>
                <div className="form-group">
                  <MultiSelectFilter
                    label="Delivery options"
                    options={DATA_PRODUCT_DELIVERY_FORMATS.map((item) => ({
                      id: item.label,
                      name: item.label,
                    }))}
                    name={`delivery_format`}
                    setValue={setValue}
                    selectedValues={getValues().delivery_format || []}
                    control={control}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <input
            className="btn-secondary-dark btn btn-secondary"
            type="button"
            value="Cancel"
            onClick={() => onHide()}
          />
          <SubmitButtonTooltip
            disabled={isLoading}
            toolTipMessage={showTooltip()}
            title={data ? 'Save' : 'Submit'}
          />
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default DataProductModal;

/**
 * Function to convert data product form data to data compatible with BE.
 * The main pourpose of this function is to map array fields form objects to
 * data_tag compatible object.
 * @param data Data product data
 * @returns Parsed data product
 * {
 * "name": "Name",
 * "description": "Description",
 * "pricemin": 1,
 * "pricemax": 1,
 * "lag_in_days": 2,
 * "history_in_months": 3,
 * "data_tags": [{ type: 'sector', value: 1 }, { type: 'delivery_format', value: 'API' }, ...]
 * }
 */
export function parseDataToSave(data: DataProductFormData) {
  interface DataProductToSave extends DataProductEntity {
    prompt_history_id?: number;
  }
  const dataParsed: DataProductToSave = {
    name: data.name,
    description: data.description,
    pricemin: data.pricemin,
    pricemax: data.pricemax,
    lag_in_days: data.lag_in_days,
    history_in_months: data.history_in_months,
    data_tags: [],
    prompt_history_id: data.prompt_history_id,
  };
  for (const key in data) {
    const propKey = key as keyof typeof DATA_PRODUCT_DEFAULT_VALUES;
    if (propKey !== 'delivery_format' && TAG_FIELDS.includes(propKey)) {
      dataParsed.data_tags = [
        ...(dataParsed.data_tags || []),
        ...(data[propKey] as Array<TagForm>).map((item) => ({
          type: key,
          value: item.value,
        })),
      ];
    } else if (propKey === 'delivery_format') {
      const deliveryFormats = data[propKey].filter(Boolean).map((item) => ({
        type: propKey,
        value: String(item.id),
      }));
      dataParsed.data_tags = [
        ...(dataParsed.data_tags || []),
        ...deliveryFormats,
      ];
    }
  }
  return dataParsed;
}

async function saveDataProduct(data: DataProductEntity) {
  const dataTagsResponse = await Promise.all(
    data.data_tags.map((item) => restFactory.post<Tag>('/data-tags', item))
  );

  const dataProductToSave = flow(
    set(
      'data_tags',
      dataTagsResponse.map((tag) => tag.id)
    )
  )(data);
  const prompt_history = dataProductToSave.prompt_history_id;
  delete dataProductToSave.prompt_history_id;
  let dataProductId;
  if (data.id) {
    const dataProduct = await restFactory.put<DataProductEntity>(
      `data-products/${data.id}`,
      omit('id')(
        Object.keys(dataProductToSave).reduce(
          (acc, key) => ({
            ...acc,
            [key]:
              dataProductToSave[key] === undefined
                ? null
                : dataProductToSave[key],
          }),
          {}
        )
      )
    );
    dataProductId = dataProduct?.id;
  } else {
    const dataProduct = await restFactory.post<DataProductEntity>(
      'data-products',
      dataProductToSave
    );
    dataProductId = dataProduct?.id;
  }
  if (prompt_history && dataProductId !== undefined) {
    createPromptHistoryRelatedEntity({
      app_label: 'user',
      model_name: 'dataproducts',
      object_id: dataProductId,
      prompt_history,
    });
  }
}
